import { GENRES, REGIONS } from "@/types/media";
import type { SortKey } from "@/utils/media";

export interface FilterState {
  region: string;
  year: string;
  genre: string;
  sort: SortKey;
}

interface FilterControlsProps {
  value: FilterState;
  years: string[];
  onChange: (next: Partial<FilterState>) => void;
  onReset: () => void;
}

const chipBase =
  "cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors";
const chipActive = "bg-primary-600 text-background-50";
const chipIdle =
  "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-700";

export default function FilterControls({
  value,
  years,
  onChange,
  onReset,
}: FilterControlsProps) {
  const sortOptions: { key: SortKey; label: string; icon: string }[] = [
    { key: "latest", label: "最新", icon: "ri-time-line" },
    { key: "popular", label: "热门", icon: "ri-fire-line" },
    { key: "rating", label: "评分", icon: "ri-star-line" },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <span className="text-xs font-semibold text-foreground-500">地区</span>
        <div className="flex flex-wrap gap-2">
          {["全部", ...REGIONS].map((region) => (
            <button
              key={region}
              type="button"
              onClick={() => onChange({ region })}
              className={`${chipBase} ${
                value.region === region ? chipActive : chipIdle
              }`}
            >
              {region}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-semibold text-foreground-500">类型</span>
        <div className="flex flex-wrap gap-2">
          {["全部", ...GENRES].map((genre) => (
            <button
              key={genre}
              type="button"
              onClick={() => onChange({ genre })}
              className={`${chipBase} ${
                value.genre === genre ? chipActive : chipIdle
              }`}
            >
              {genre}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-xs font-semibold text-foreground-500">
            年份
            <select
              value={value.year}
              onChange={(e) => onChange({ year: e.target.value })}
              className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-sm text-foreground-800 outline-none focus:border-primary-400"
            >
              <option value="全部">全部</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
              <option value="更早">更早</option>
            </select>
          </label>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-foreground-500">排序</span>
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => onChange({ sort: opt.key })}
                  className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
                    value.sort === opt.key
                      ? "bg-accent-500 font-semibold text-accent-950"
                      : "text-foreground-600 hover:text-primary-700"
                  }`}
                >
                  <i className={opt.icon} aria-hidden="true" />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onReset}
          className="flex w-fit cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-600"
        >
          <i className="ri-refresh-line" aria-hidden="true" />
          重置筛选
        </button>
      </div>
    </div>
  );
}